import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Users, Wand2, Heart, Cake, Rocket, Sun, Leaf, Moon, School, MapPin, PartyPopper, GraduationCap, Pencil } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Star, Sparkle, FloatingDecor, Confetti } from "@/components/Decor";
import Terry from "@/components/Terry";
import TerryGuide from "@/components/TerryGuide";
import client, { mediaUrl } from "@/lib/api";
import { COMPANY } from "@/constants/company";

const themeMeta = {
    birthday: { icon: Cake, colors: "from-rose to-buttercup" },
    space: { icon: Rocket, colors: "from-lavender to-sky" },
    dinosaur: { icon: Leaf, colors: "from-mint to-buttercup" },
    world: { icon: MapPin, colors: "from-sky to-mint" },
    forest: { icon: Leaf, colors: "from-mint to-lavender" },
    bedtime: { icon: Moon, colors: "from-lavender to-plum" },
    school: { icon: School, colors: "from-buttercup to-rose" },
    trip: { icon: Sun, colors: "from-peach to-sky" },
    festivals: { icon: PartyPopper, colors: "from-rose to-lavender" },
    educational: { icon: GraduationCap, colors: "from-mint to-sky" },
    custom: { icon: Pencil, colors: "from-bubblegum to-lavender" },
};

const rise = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Landing() {
    const terry = useRef(null);
    const nav = useNavigate();
    const [burst, setBurst] = useState(0);
    const [themes, setThemes] = useState([]);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await client.get("/themes");
                if (!alive) return;
                setThemes(data.themes.map((t) => ({ ...t, ...(themeMeta[t.key] || {}) })));
            } catch {}
        };
        load();
        // Poll every 6s for a minute so newly generated covers appear on the landing.
        const poll = setInterval(load, 6000);
        setTimeout(() => clearInterval(poll), 60000);
        return () => { alive = false; clearInterval(poll); };
    }, []);

    return (
        <div className="min-h-screen magic-bg text-plum overflow-x-hidden">
            <Navbar />
            <TerryGuide ref={terry} onCreate={() => nav("/auth?mode=signup")} />

            {/* ================= HERO ================= */}
            <section className="relative max-w-6xl mx-auto px-6 pt-4 pb-14 md:pt-8 md:pb-24">
                <FloatingDecor />

                <div className="relative grid lg:grid-cols-2 gap-12 items-center pt-8">
                    <motion.div initial="hidden" animate="show" variants={rise} data-testid="hero-copy" className="relative z-10">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-plum/10 px-4 py-1.5 text-sm text-plum shadow-sm mb-6"
                        >
                            <Sparkle size={14} /> A magical keepsake for tiny humans
                        </motion.div>

                        <h1 className="font-display text-[3.4rem] md:text-7xl leading-[0.98] tracking-tight text-plum">
                            Every child is the hero <br />
                            of their own <span className="text-bubblegum">story.</span>
                        </h1>

                        <div className="script-note text-3xl md:text-4xl mt-3 -rotate-2">
                            the same smiles, page after page
                        </div>

                        <p className="mt-5 text-lg text-plum/70 max-w-lg leading-relaxed">
                            Create a magical personalised storybook starring your little hero.
                            Upload a photo of your child and their favourite people — Terry
                            helps weave them into a hand-illustrated book you can read tonight
                            and keep forever. ✨
                        </p>

                        <div className="mt-8 flex flex-wrap gap-4 items-center">
                            <Link to="/auth?mode=signup" id="hero-cta" onClick={() => setBurst(Date.now())}>
                                <motion.button
                                    whileHover={{ scale: 1.05, rotate: -1 }}
                                    whileTap={{ scale: 0.96 }}
                                    className="btn-sticker text-lg relative"
                                    data-testid="hero-cta-primary"
                                >
                                    ✨ Create My Story
                                </motion.button>
                            </Link>
                            <Link to="/samples">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    className="btn-sticker sky text-lg"
                                    data-testid="hero-cta-secondary"
                                >
                                    See real pages
                                </motion.button>
                            </Link>
                            <Confetti trigger={burst} />
                        </div>

                        <div className="mt-6 md:mt-10 flex items-center gap-3 text-sm text-plum/60">
                            <div className="flex -space-x-2">
                                {["#FDA4AF", "#7DD3FC", "#86EFAC", "#C4B5FD"].map((c, i) => (
                                    <motion.div
                                        key={i}
                                        animate={{ y: [0, -3, 0] }}
                                        transition={{ duration: 3 + i, repeat: Infinity }}
                                        className="w-9 h-9 rounded-full border-2 border-white shadow-sm"
                                        style={{ background: c }}
                                    />
                                ))}
                            </div>
                            <span>Loved by 1,200+ tiny readers this year</span>
                        </div>
                    </motion.div>

                    {/* Right hero visual — two real pages from a real book, tilted
                        like they were just set down on the table. */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.15 }}
                        className="relative"
                    >
                        <div className="relative max-w-[440px] mx-auto aspect-square">
                            <Terry pose="hero"
                                   className="terry-static absolute z-20 left-0 sm:-left-6 lg:-left-10 bottom-2 sm:bottom-3 lg:bottom-1
                                              h-[124px] sm:h-[158px] lg:h-[182px] w-auto max-w-none"
                                   style={{ animationDuration: "4.6s" }} />
                            {/* Big page, tilted left */}
                            <div className="absolute left-[4%] top-[6%] w-[74%] card-cloud p-3 -rotate-[4deg] anim-bob-slow">
                                <img
                                    src="/demo/noah-space-cover.jpg"
                                    alt="The cover of Noah's Space Voyage, drawn in Pixar style"
                                    className="w-full aspect-square object-cover rounded-2xl"
                                    draggable={false}
                                />
                                {/* Kept clear of the smaller page floating over the
                                    bottom-right corner on narrow screens. */}
                                {/* The card's own rotate makes a stacking context, so this
                                    cannot simply paint over the smaller page tucked at its
                                    corner — it gets a lane between Terry and that card
                                    instead, and wraps inside it. */}
                                <div className="max-w-[62%] lg:max-w-[210px] lg:pl-[86px]">
                                    <div className="font-script text-xl sm:text-2xl text-plum mt-2 px-1 leading-tight">
                                        Noah&apos;s Space Voyage
                                    </div>
                                    <div className="font-display text-[11px] text-plum/50 px-1 mt-1">
                                        12 pages · hardcover keepsake
                                    </div>
                                </div>
                            </div>

                            {/* Small page, tilted right */}
                            <div className="absolute -right-2 bottom-0 w-[42%] card-cloud p-2.5 rotate-[6deg] anim-bob-slow"
                                 style={{ animationDelay: "1.2s", animationDuration: "9s" }}>
                                <img
                                    src="/demo/noah-space-rocket.jpg"
                                    alt="Another page from the same book — Noah and Byte in the rocket"
                                    className="w-full aspect-square object-cover rounded-xl"
                                    draggable={false}
                                />
                            </div>

                            {/* Sticker chips */}
                            <motion.div
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 4, repeat: Infinity }}
                                className="absolute left-0 sm:-left-6 top-[2%] bg-white rounded-2xl px-3 py-2 shadow-[0_12px_26px_rgba(45,27,78,0.14)]
                                           flex items-center gap-2 text-[11px] sm:text-sm whitespace-nowrap z-10"
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-bubblegum shrink-0" /> Same faces every page
                            </motion.div>
                            <motion.div
                                animate={{ y: [0, 6, 0] }}
                                transition={{ duration: 4.5, repeat: Infinity, delay: 1.5 }}
                                className="absolute right-0 sm:-right-4 top-[42%] bg-white rounded-2xl px-3 py-2 shadow-[0_12px_26px_rgba(45,27,78,0.14)]
                                           flex items-center gap-2 text-[11px] sm:text-sm whitespace-nowrap z-10"
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-sky shrink-0" /> Ready in minutes
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ================= HOW IT WORKS ================= */}
            <section id="how" className="relative max-w-6xl mx-auto px-6 py-14 md:py-24">
                <div className="text-center mb-10 md:mb-16">
                    <div className="inline-flex items-center gap-2 rounded-full bg-mint/60 border-2 border-plum/5 px-4 py-1.5 text-sm text-plum shadow-sm mb-5">
                        <Wand2 className="w-4 h-4" /> The Tales with Terry method
                    </div>
                    <h2 className="font-display text-5xl md:text-6xl text-plum">
                        Four gentle steps, <br /> one book you'll <span className="rainbow-underline">keep forever.</span>
                    </h2>
                    <div className="script-note text-3xl mt-4">no design skills, no fuss</div>
                </div>

                {/* Terry sails across on his paper plane between the promise and
                    the steps that deliver it. */}
                <div className="terry-static relative h-24 -mt-7 mb-1 overflow-hidden pointer-events-none" aria-hidden>
                    <Terry pose="flying" anim="none" className="absolute bottom-0 h-[92px] w-auto anim-fly-across" />
                </div>

                <div className="grid md:grid-cols-4 gap-5">
                    {[
                        { icon: Users, title: "Upload the cast", copy: "Add your child, then their favourite people — one at a time.", bg: "bg-rose/40" },
                        { icon: Wand2, title: "Choose a theme", copy: "Ten magical worlds — or write your very own storyline.", bg: "bg-buttercup/60" },
                        { icon: Sparkle, title: "Terry weaves magic", copy: "CharacterLock™ keeps every face and outfit the same, page after page.", bg: "bg-mint/50" },
                        { icon: BookOpen, title: "Read & keep", copy: "Flip through in the browser, or order a hardcover keepsake.", bg: "bg-sky/50" },
                    ].map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            whileHover={{ y: -6, rotate: i % 2 ? 1 : -1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1, duration: 0.6 }}
                            className="card-cloud p-6 relative"
                            data-testid={`how-step-${i}`}
                        >
                            <div className="absolute -top-4 -right-4">
                                <motion.div animate={{ rotate: [0, 12, 0] }} transition={{ duration: 5, repeat: Infinity, delay: i * 0.4 }}>
                                    <Star size={26} />
                                </motion.div>
                            </div>
                            <div className={`w-14 h-14 rounded-2xl grid place-items-center ${s.bg} mb-5`}>
                                <s.icon className="w-6 h-6 text-plum" strokeWidth={2.4} />
                            </div>
                            <div className="font-display text-2xl mb-2 text-plum">{s.title}</div>
                            <p className="text-sm text-plum/60 leading-relaxed">{s.copy}</p>
                            <div className="mt-4 text-xs font-display text-bubblegum">Step {i + 1}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ================= THEME GALLERY ================= */}
            <section id="themes" className="relative max-w-6xl mx-auto px-6 py-12 md:py-20">
                <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-6">
                    <div className="flex items-end gap-4">
                        {/* Terry weighing up the worlds, question mark and all. */}
                        <div className="terry-static relative shrink-0" aria-hidden>
                            <Terry pose="thinking" anim="none" className="block h-[72px] sm:h-[104px] w-auto max-w-none anim-ponder" />
                            <span className="absolute -top-1.5 -right-2.5 font-display text-[26px] text-lavender animate-twinkle">?</span>
                        </div>
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-lavender/60 border-2 border-plum/5 px-4 py-1.5 text-sm text-plum shadow-sm mb-4">
                            <Sparkle size={14} /> The Collection
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl text-plum">
                            Eleven magical worlds
                        </h2>
                        <div className="script-note text-3xl mt-2">tap one — you can change it later</div>
                        <p className="text-plum/60 mt-3 max-w-lg">Pick a ready-made story world, or scribble your very own idea.</p>
                    </div>
                    </div>
                    <Link to="/auth?mode=signup" className="hidden md:inline">
                        <button className="btn-sticker mint" data-testid="themes-cta">Start with any theme →</button>
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {themes.map((t, i) => {
                        const Icon = t.icon || Pencil;
                        return (
                        <motion.div
                            key={t.key}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            whileHover={{ y: -6, rotate: i % 2 ? 1.5 : -1.5, scale: 1.02 }}
                            viewport={{ once: true }}
                            transition={{ delay: (i % 4) * 0.06 }}
                            className={`card-cloud aspect-[4/5] relative overflow-hidden cursor-pointer ${!t.cover_url ? `bg-gradient-to-br ${t.colors || "from-rose to-buttercup"}` : ""}`}
                            data-testid={`theme-card-${t.key}`}
                        >
                            {t.cover_url && (
                                <img src={mediaUrl(t.cover_url)} alt={t.name} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                            )}
                            <div className="absolute top-4 right-4">
                                <motion.div animate={{ rotate: [0, 15, 0] }} transition={{ duration: 4, repeat: Infinity, delay: i * 0.2 }}>
                                    <Star size={20} />
                                </motion.div>
                            </div>
                            {!t.cover_url && (
                                <div className="absolute inset-0 grid place-items-center">
                                    <Icon className="w-16 h-16 text-white drop-shadow-md" strokeWidth={1.8} />
                                </div>
                            )}
                            <div className="absolute top-3 left-3 text-[10px] tracking-[0.15em] uppercase bg-white/90 text-plum px-2 py-1 rounded-full font-semibold flex items-center gap-1 shadow-sm">
                                <Sparkle size={10} /> AI-illustrated
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-plum/85 via-plum/40 to-transparent text-white">
                                <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Theme</div>
                                <div className="font-display text-xl mt-0.5">{t.name}</div>
                            </div>
                        </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* ================= SAMPLE BOOK PREVIEW ================= */}
            <section id="peek" className="relative max-w-6xl mx-auto px-6 py-12 md:py-20">
                <div className="grid md:grid-cols-2 gap-14 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="relative pb-16 md:pb-8"
                    >
                        <div className="card-cloud p-4 -rotate-[3deg]">
                            <img src="/demo/noah-forest-cover.jpg" alt="The cover of Noah's Whispering Forest, sculpted in claymation"
                                 className="w-full aspect-square object-cover rounded-2xl" draggable={false} />
                            <div className="mt-3 px-1 max-w-[62%] md:max-w-none">
                                <div className="font-script text-2xl md:text-3xl text-plum leading-tight">Noah&apos;s Whispering Forest</div>
                                <div className="font-display text-xs mt-1 text-plum/50">Claymation · 12 pages</div>
                            </div>
                        </div>
                        <motion.div
                            animate={{ y: [0, -10, 0], rotate: [5, 2, 5] }}
                            transition={{ duration: 7, repeat: Infinity }}
                            className="absolute -right-3 md:-right-8 -bottom-4 md:-bottom-8 card-cloud p-2.5 md:p-3 w-[38%] max-w-[150px] md:max-w-[220px]"
                        >
                            <img src="/demo/noah-forest-path.jpg" alt="A page from the same book — Noah and Moss on the mossy path"
                                 className="w-full aspect-square object-cover rounded-xl" draggable={false} />
                        </motion.div>
                        <Terry pose="heart"
                               className="terry-static absolute -left-6 lg:-left-14 -bottom-4 lg:-bottom-6 h-[96px] lg:h-[150px] w-auto max-w-none"
                               style={{ animationDuration: "5.2s" }} />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="inline-flex items-center gap-2 rounded-full bg-buttercup/70 border-2 border-plum/5 px-4 py-1.5 text-sm text-plum shadow-sm mb-4">
                            <BookOpen className="w-4 h-4" /> A peek inside
                        </div>
                        <h2 className="font-display text-5xl text-plum">Every page is a hug.</h2>
                        <div className="script-note text-3xl mt-2 mb-5">these are real pages, from a real book</div>
                        <p className="text-plum/70 leading-relaxed max-w-md text-lg">
                            Your child sees <em>themselves</em> as the hero. Their Nani, their Chachu, even their kitty —
                            all illustrated in the same warm storybook style, page after page. It's the book they'll ask for
                            at bedtime for years.
                        </p>
                        <div className="mt-8 flex gap-3 flex-wrap">
                            {["Same faces", "Same voice", "Same magic", "Every page"].map((t, i) => (
                                <motion.span key={t} whileHover={{ scale: 1.05 }}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-plum/8 px-3 py-1.5 text-sm text-plum shadow-sm">
                                    <span className="w-2 h-2 rounded-full" style={{ background: ["#FF6B9D", "#7DD3FC", "#86EFAC", "#C4B5FD"][i] }} />
                                    {t}
                                </motion.span>
                            ))}
                        </div>
                        <Link to="/samples" className="inline-block mt-8">
                            <button className="btn-sticker sky" data-testid="peek-samples-cta">See more real pages</button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ================= TESTIMONIAL ================= */}
            <section className="relative max-w-4xl mx-auto px-6 py-14 md:py-24 text-center">
                {/* Terry has heard this one before. */}
                <div className="terry-static hidden lg:block absolute -left-8 bottom-8 w-[176px] pointer-events-none" aria-hidden>
                    <Terry pose="sleeping" anim="snooze" className="w-full h-auto" />
                    <span className="absolute -top-1.5 right-4 font-display text-xl text-lavender anim-zzz">z</span>
                    <span className="absolute -top-1.5 right-8 font-display text-[15px] text-lavender anim-zzz"
                          style={{ animationDelay: "1.2s" }}>z</span>
                </div>
                <div className="mx-auto mb-8 flex justify-center gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <motion.span key={i}
                            animate={{ scale: [1, 1.2, 1], rotate: [0, 12, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: i * 0.15 }}>
                            <Star size={22} />
                        </motion.span>
                    ))}
                </div>
                <blockquote className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-plum">
                    "My daughter cried when she saw Nani in the book. <br />
                    <span className="text-bubblegum">In the good way.</span>"
                </blockquote>
                <div className="mt-8 text-sm text-plum/60">— Aditi, Bengaluru • Birthday Wonder edition</div>
            </section>

            {/* ================= PRICING ================= */}
            <section id="pricing" className="relative max-w-5xl mx-auto px-6 py-12 md:py-20">
                <div className="text-center mb-14">
                    <div className="inline-flex items-center gap-2 rounded-full bg-rose/50 border-2 border-plum/5 px-4 py-1.5 text-sm text-plum shadow-sm mb-4">
                        <Heart className="w-4 h-4 fill-bubblegum text-bubblegum" /> Keepsake pricing
                    </div>
                    <h2 className="font-display text-5xl md:text-6xl text-plum">Three ways to keep the story.</h2>
                    <div className="script-note text-3xl mt-3">one night, one shelf, or one whole year</div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    <motion.div whileHover={{ y: -6, rotate: -1 }} className="card-cloud p-8 relative overflow-hidden" data-testid="pricing-pdf">
                        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-buttercup/50" />
                        <div className="text-xs uppercase tracking-[0.2em] text-plum/60 relative">Digital</div>
                        <div className="font-display text-3xl mt-2 relative">PDF Download</div>
                        <div className="font-display text-5xl text-bubblegum mt-4 relative">₹399</div>
                        <p className="text-sm text-plum/60 mt-2 relative">Instant. Print at home or read on-screen forever.</p>
                    </motion.div>
                    <motion.div whileHover={{ y: -6, rotate: 1 }} className="card-cloud p-8 relative overflow-hidden bg-gradient-to-br from-white to-lavender/30" data-testid="pricing-hardcover">
                        <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] bg-bubblegum text-white px-3 py-1 rounded-full shadow-sm">Best gift</div>
                        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-mint/40" />
                        <div className="text-xs uppercase tracking-[0.2em] text-plum/60 relative">Physical</div>
                        <div className="font-display text-3xl mt-2 relative">Hardcover Book</div>
                        <div className="font-display text-5xl text-bubblegum mt-4 relative">₹799</div>
                        <p className="text-sm text-plum/60 mt-2 relative">Printed, bound and shipped to your door.</p>
                    </motion.div>
                </div>

                {/* The Year Book gets its own band rather than a third price card:
                    it is a different kind of thing — a habit, not a purchase — and
                    a card with a number on it explains none of that. */}
                <motion.div whileHover={{ y: -4 }}
                    className="card-cloud mt-6 max-w-3xl mx-auto p-8 md:p-10 relative overflow-hidden
                               bg-gradient-to-br from-white via-white to-rose/30"
                    data-testid="pricing-yearbook">
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-buttercup/30" />
                    <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] bg-plum text-white px-3 py-1 rounded-full shadow-sm">
                        The keepsake
                    </div>
                    <div className="relative">
                        <div className="text-xs uppercase tracking-[0.2em] text-plum/60">One whole year</div>
                        <div className="font-display text-4xl md:text-5xl mt-2 text-plum">The Year Book</div>
                        <div className="script-note text-2xl mt-1">one question a week, one book at the end</div>
                        {/* Three lengths of the same thing. Cheapest first, so the
                            entry price is what a hesitant parent sees. */}
                        <div className="grid sm:grid-cols-3 gap-3 mt-6">
                            {[
                                ["3 months", "₹999", "13 questions · 13-page book"],
                                ["6 months", "₹1,599", "26 questions · 26-page book"],
                                ["12 months", "₹2,599", "52 questions · 52-page book"],
                            ].map(([label, price, detail], i) => (
                                <div key={label}
                                    className={`rounded-2xl p-4 border-2 ${
                                        i === 1 ? "border-bubblegum bg-white" : "border-plum/10 bg-white/60"}`}>
                                    <div className="text-xs uppercase tracking-[0.18em] text-plum/55">{label}</div>
                                    <div className="font-display text-3xl text-bubblegum mt-1">{price}</div>
                                    <div className="text-xs text-plum/55 mt-1">{detail}</div>
                                    {i === 1 && (
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-bubblegum mt-2">
                                            Most families
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="grid sm:grid-cols-3 gap-5 mt-8 text-sm">
                            <div>
                                <div className="font-display text-lg text-plum">Every week, one question</div>
                                <p className="text-plum/60 mt-1">
                                    “What made them laugh this week?” We email it. You answer in a
                                    sentence. Miss a week and it simply waits.
                                </p>
                            </div>
                            <div>
                                <div className="font-display text-lg text-plum">Add that week's photo</div>
                                <p className="text-plum/60 mt-1">
                                    Each page is drawn from the photo of that week, so your child
                                    grows as you turn the pages.
                                </p>
                            </div>
                            <div>
                                <div className="font-display text-lg text-plum">A hardcover at the end</div>
                                <p className="text-plum/60 mt-1">
                                    At the end, every answer is a page — in the order it really
                                    happened. Printed and posted to you.
                                </p>
                            </div>
                        </div>

                        <p className="text-sm text-plum/55 mt-8 max-w-xl">
                            Not a story we invented. The year you actually had — written down one
                            line at a time, before you forget it.
                        </p>

                        <Link to="/auth?mode=signup" className="inline-block mt-6">
                            <motion.button whileHover={{ scale: 1.04, rotate: -1 }} whileTap={{ scale: 0.96 }}
                                className="btn-sticker" data-testid="yearbook-cta">
                                Start the Year Book →
                            </motion.button>
                        </Link>
                    </div>
                </motion.div>
                {/* Terry strolls off with the finished book — four frames, swapped
                    on a step timing so his feet actually land. */}
                <div className="terry-static relative h-24 mt-3 overflow-hidden pointer-events-none" aria-hidden>
                    <div className="absolute bottom-0 anim-walk-path">
                        <div className="relative h-[88px] w-[78px] anim-walk-bounce">
                            {[["walk", "0s"], ["walk2", "-0.51s"], ["walk3", "-0.34s"], ["walk4", "-0.17s"]].map(
                                ([frame, delay], i) => (
                                <img key={frame} src={`/terry/terry-${frame}.png`} alt="" draggable={false}
                                     className="absolute bottom-0 left-0 h-[88px] w-auto anim-walk-frame drop-shadow-[0_10px_14px_rgba(45,27,78,0.14)]"
                                     style={{ opacity: i === 0 ? 1 : 0, animationDelay: delay }} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="text-center mt-12">
                    <Link to="/auth?mode=signup">
                        <motion.button
                            whileHover={{ scale: 1.05, rotate: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn-sticker text-lg"
                            data-testid="pricing-cta"
                        >
                            ✨ Begin your story
                        </motion.button>
                    </Link>
                </div>
            </section>

            {/* ================= FINAL CTA ================= */}
            <section className="relative max-w-6xl mx-auto px-6 pt-5 pb-24">
                <div id="final-cta" className="relative card-cloud px-8 py-12 md:p-14 text-center overflow-hidden">
                    <div className="absolute -top-14 -left-10 w-52 h-52 rounded-full bg-sunrise/60" />
                    <div className="relative">
                        <h2 className="font-display text-4xl md:text-5xl text-plum">Ready to create your first adventure?</h2>
                        <div className="script-note text-3xl mt-2">Terry has the pencils sharpened</div>
                        <Link to="/auth?mode=signup" className="inline-block mt-7">
                            <motion.button whileHover={{ scale: 1.04, rotate: -1 }} whileTap={{ scale: 0.96 }}
                                className="btn-sticker text-xl" data-testid="final-cta-btn">
                                Create My Story
                            </motion.button>
                        </Link>
                    </div>
                    <Terry pose="point"
                           className="terry-static absolute right-1 md:right-6 -bottom-1.5 h-[104px] sm:h-[140px] md:h-[190px] w-auto max-w-none"
                           style={{ animationDuration: "4.8s" }} />
                </div>
            </section>

            {/* ================= FOOTER ================= */}
            <footer className="border-t border-plum/10 bg-paper/70 mt-4">
                <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-5">
                    <div className="flex items-center gap-2.5">
                        <Terry pose="wave" className="terry-static h-[52px] w-auto max-w-none" style={{ animationDuration: "5s" }} />
                        <img src="/terry/wordmark.png" alt={COMPANY.name} className="h-[26px] w-auto" draggable={false} />
                    </div>
                    <div className="text-[13px] text-plum/55">
                        Every child is the hero of their own story. © {new Date().getFullYear()} {COMPANY.name}
                    </div>
                    <button onClick={() => terry.current?.replay()}
                        className="font-display text-sm text-terracotta border-b-2 border-dashed border-terracotta/40"
                        data-testid="footer-replay-tour">
                        Meet Terry again
                    </button>
                </div>
                <div className="max-w-6xl mx-auto px-6 pb-10 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-plum/60"
                     data-testid="footer-legal">
                    <Link to="/terms" className="hover:text-plum underline-offset-4 hover:underline">Terms</Link>
                    <Link to="/privacy" className="hover:text-plum underline-offset-4 hover:underline">Privacy</Link>
                    <Link to="/refund-policy" className="hover:text-plum underline-offset-4 hover:underline">Payments &amp; refunds</Link>
                    <Link to="/contact" className="hover:text-plum underline-offset-4 hover:underline">Contact</Link>
                </div>
            </footer>
        </div>
    );
}
